import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { getEffectiveMoq, isFactoryProductListing } from '../../util/factoryListing';
import { saveProduct, unsaveProduct, checkSavedProduct } from '../../util/api';

import css from './SaveProductButton.module.css';

/**
 * Save / unsave a factory product listing (Mongo).
 * Redirects to login when signed out.
 *
 * @component
 */
const SaveProductButton = props => {
  const {
    listing,
    className,
    rootClassName,
    variant = 'button', // 'button' | 'icon'
  } = props;

  const intl = useIntl();
  const history = useHistory();
  const routes = useRouteConfiguration();
  const currentUser = useSelector(state => state.user?.currentUser);

  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState(false);

  const listingId = listing?.id?.uuid || null;
  const publicData = listing?.attributes?.publicData || {};
  const isFactoryProduct = isFactoryProductListing(publicData);
  const buyerUserId = currentUser?.id?.uuid || null;

  useEffect(() => {
    if (!listingId || !buyerUserId || !isFactoryProduct) {
      setChecked(true);
      return undefined;
    }
    let cancelled = false;
    checkSavedProduct(buyerUserId, listingId)
      .then(data => {
        if (!cancelled) setSaved(!!data.saved);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [listingId, buyerUserId, isFactoryProduct]);

  if (!isFactoryProduct || !listingId) return null;

  const buildPayload = () => {
    const tiers = Array.isArray(publicData.priceTiers) ? publicData.priceTiers : [];
    const sorted = tiers
      .slice()
      .filter(t => Number.isFinite(Number(t.minQty)))
      .sort((a, b) => Number(a.minQty) - Number(b.minQty));
    const basePrice = sorted[0]?.price != null ? Number(sorted[0].price) : null;
    const firstImage = listing?.images?.[0];
    const variants = firstImage?.attributes?.variants || {};
    const imageUrl =
      variants['listing-card']?.url ||
      variants['listing-card-2x']?.url ||
      variants.default?.url ||
      null;

    return {
      buyerUserId,
      listingId,
      mongoProductId: publicData.mongoProductId || null,
      productName: listing?.attributes?.title || '',
      productSku: publicData.sku || '',
      category: publicData.category || '',
      industry: publicData.industry || '',
      agentName: publicData.agentName || '',
      agentUserId: publicData.agentUserId || null,
      factoryUserId: listing?.author?.id?.uuid || null,
      unit: publicData.unitOfMeasurement || 'pcs',
      moq: getEffectiveMoq(publicData),
      basePrice,
      currency: listing?.attributes?.price?.currency || 'USD',
      imageUrl,
    };
  };

  const goLogin = () => {
    const returnTo = history.location?.pathname + (history.location?.search || '');
    history.push(createResourceLocatorString('LoginPage', routes, {}, { from: returnTo }));
  };

  const toggle = async e => {
    e.preventDefault();
    e.stopPropagation();

    if (!buyerUserId) {
      goLogin();
      return;
    }

    setBusy(true);
    try {
      if (saved) {
        await unsaveProduct(buyerUserId, listingId);
        setSaved(false);
      } else {
        await saveProduct(buildPayload());
        setSaved(true);
      }
    } catch (err) {
      console.error('[SaveProductButton]', err);
    } finally {
      setBusy(false);
    }
  };

  const classes = classNames(
    rootClassName || (variant === 'icon' ? css.iconRoot : css.root),
    className,
    {
      [css.saved]: saved,
      [css.busy]: busy,
    }
  );

  const label = saved
    ? intl.formatMessage({ id: 'SaveProductButton.saved' })
    : intl.formatMessage({ id: 'SaveProductButton.save' });

  return (
    <button
      type="button"
      className={classes}
      onClick={toggle}
      disabled={busy || !checked}
      aria-pressed={saved}
      aria-label={label}
      title={label}
    >
      <svg className={css.heart} width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        {saved ? (
          <path
            fill="currentColor"
            d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
          />
        ) : (
          <path
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            d="M12.1 8.64l-.1.1-.11-.11C10.14 6.6 7.16 5.5 4.98 7.04 3.2 8.3 3 10.78 4.7 12.6L12 20l7.3-7.4c1.7-1.82 1.5-4.3-.28-5.56-2.18-1.54-5.16-.44-6.92 1.6z"
          />
        )}
      </svg>
      {variant === 'button' ? (
        <span className={css.label}>
          {saved ? (
            <FormattedMessage id="SaveProductButton.saved" />
          ) : (
            <FormattedMessage id="SaveProductButton.save" />
          )}
        </span>
      ) : null}
    </button>
  );
};

export default SaveProductButton;
